import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getLiveMatches, formatMapName, LiveMatch } from '../../services/LiveService';
import '../../styles/live.css';
import Loading from '../../components/Loading';
import useLocalStorage from 'use-local-storage';

const LiveMatchesPage: React.FC = () => {
  const [matches, setMatches] = useState<LiveMatch[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDark] = useLocalStorage<boolean>("isDark", true);

  useEffect(() => {
    const fetchLiveMatches = async () => {
      try {
        setLoading(true);
        const data = await getLiveMatches();
        setMatches(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching live matches:', err);
        setError('No live matches currently available');
        setMatches([]);
      } finally {
        setLoading(false);
      }
    };

    // Fetch matches immediately
    fetchLiveMatches();

    // Then set up polling every 30 seconds
    const interval = setInterval(fetchLiveMatches, 30000);

    // Clean up on unmount
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={`live-matches-container ${isDark ? 'theme-dark' : 'theme-light'}`}>
      <h1>Live Matches</h1>
      
      {matches.length === 0 ? (
        <div className="alert alert-info">
          {error || 'No live matches currently available.'}
        </div>
      ) : (
        <div className="live-matches-grid">
          {matches.map((match) => (
            <Link 
              to={`/live/${match.matchId}`} 
              key={match.matchId}
              className={`live-match-card ${isDark ? 'dark-mode' : 'light-mode'}`}
            >
              <div className={`match-header ${isDark ? 'dark-mode' : 'light-mode'}`}>
                <span className="map-name">{formatMapName(match.mapName)}</span>
                {match.isStreaming && (
                  <span className="live-badge">LIVE</span>
                )}
              </div>
              
              <div className="teams-container">
                <div className="team team-ct">
                  <div className="team-logo-container">
                    {match.ctTeam.logoUrl && (
                      <img 
                        src={match.ctTeam.logoUrl} 
                        alt={match.ctTeam.teamName} 
                        className="team-logo"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                  <span className="team-name">{match.ctTeam.teamName}</span>
                  <span className="team-score">{match.ctTeam.score}</span>
                </div>
                
                <div className="team team-t">
                  <div className="team-logo-container">
                    {match.tTeam.logoUrl && (
                      <img 
                        src={match.tTeam.logoUrl} 
                        alt={match.tTeam.teamName} 
                        className="team-logo"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                  <span className="team-name">{match.tTeam.teamName}</span>
                  <span className="team-score">{match.tTeam.score}</span>
                </div>
              </div>
              
              <div className={`match-footer ${isDark ? 'dark-mode' : 'light-mode'}`}>
                <span className="round-info">Round {match.currentRound}</span>
                {match.isStreaming && (
                  <span className="streamer-info">
                    <i className="bi bi-broadcast"></i> {match.streamerName}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveMatchesPage;